/**
 * Self-registration lifecycle for managed stores (C2/C3).
 *
 * `create_store` registers itself here when `auto.populate || auto.subscribe`
 * is set. The auth layer drives the lifecycle:
 *   - on_auth            → start_all_managed()   (populate + subscribe + prune)
 *   - on_before_sign_out → reset_all_managed()   (clear tables + unsubscribe)
 *   - app foreground     → refresh_all_managed() (incremental re-pull)
 *
 * Registration is idempotent: re-registering the same store name replaces the
 * entry (a store module is a singleton, so this only happens on HMR).
 */

type ManagedStore = {
	start: () => Promise<void>;
	refresh: () => Promise<unknown>;
	reset: () => Promise<void>;
};

const managed = new Map<string, ManagedStore>();

export function register_managed_store(name: string, store: ManagedStore): void {
	managed.set(name, store);
}

export function unregister_managed_store(name: string): void {
	managed.delete(name);
}

export async function start_all_managed(): Promise<void> {
	await Promise.all([...managed.values()].map((store) => store.start()));
}

export async function refresh_all_managed(): Promise<void> {
	await Promise.all([...managed.values()].map((store) => store.refresh()));
}

export async function reset_all_managed(): Promise<void> {
	await Promise.all([...managed.values()].map((store) => store.reset()));
}

export function managed_store_count(): number {
	return managed.size;
}

export function reset_managed_stores_for_tests(): void {
	managed.clear();
}